import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { environment } from '../../../../environment/environment';
import { RiskyIdNumberItem } from '../hooks/useRiskyIdNumbers';
import { Drawer } from '../../../../components/drawers';
import toast from '../../../../utils/toast';
import RiskyIdFormFields from './RiskyIdFormFields';

interface EditRiskyIdFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  riskyId: RiskyIdNumberItem | null;
}

const EditRiskyIdForm: React.FC<EditRiskyIdFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  riskyId
}) => {
  const [idNumber, setIdNumber] = useState('');
  const [reason, setReason] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setIdNumber(riskyId?.id_number || '');
    setReason(riskyId?.reason || '');
    setActive(riskyId?.active !== undefined ? riskyId.active : true);
    onClose();
  };

  useEffect(() => {
    if (riskyId) {
      setIdNumber(riskyId.id_number || '');
      setReason(riskyId.reason || '');
      setActive(riskyId.active !== undefined ? riskyId.active : true);
    }
  }, [riskyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!riskyId) return;

    setLoading(true);

    try {
      const payload = {
        id_number: idNumber,
        reason,
        active
      };
      await axios.put(`${environment.baseUrl}/risky-id-numbers/${riskyId.id}/update/`, payload);
      toast.success('Risky ID number updated successfully');
      onSuccess();
      handleClose();
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.id_number) {
        toast.error('This ID number has already been flagged');
      } else {
        toast.error(err.response?.data?.message || 'Failed to update risky ID number');
      }
    } finally {
      setLoading(false);
    }
  };

  const isValid = idNumber.trim().length > 0;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Drawer.Header title="Edit Risky ID Number" onClose={handleClose} />

      <Drawer.Content>
        <form onSubmit={handleSubmit}>
          <RiskyIdFormFields
            idNumber={idNumber}
            reason={reason}
            active={active}
            onIdNumberChange={setIdNumber}
            onReasonChange={setReason}
            onActiveChange={setActive}
            isIdNumberDisabled={false}
          />
        </form>
      </Drawer.Content>

      <Drawer.Footer fullWidth>
        <Drawer.PrimaryButton
          type="submit"
          onClick={handleSubmit}
          disabled={!isValid || loading}
        >
          {loading ? 'Updating...' : 'Update'}
        </Drawer.PrimaryButton>
      </Drawer.Footer>
    </Drawer>
  );
};

export default EditRiskyIdForm;